import { FaireMarketIcon } from "@faire/deprecated-retailer-visitor-shared/features/FaireMarkets/FaireMarketIcon";
import { bpsToPercent } from "@faire/deprecated-retailer-visitor-shared/lib/bpsToDecimalAndPercent";
import { useMarketCurrentEvent } from "@faire/deprecated-retailer-visitor-shared/stores/domain/Events/FaireMarkets/useMarketCurrentEvent";
import { useMarketRangeDates } from "@faire/deprecated-retailer-visitor-shared/stores/domain/Events/FaireMarkets/useMarketRangeDates";
import { useMatchedDiscountCapBps } from "@faire/deprecated-retailer-visitor-shared/stores/domain/Events/FaireMarkets/useMatchedDiscountCapBps";
import { useViewport } from "@faire/web/common/hooks/useViewport";
import { Button } from "@faire/web/slate-deprecated/Button";
import { Column, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { getEventTheme } from "@faire/web/slate/Theme/eventsThemeColors";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";
import styled from "styled-components";

interface IProps {
  onClick: () => void;
}

export const BrandsLandingPageFaireMarket: React.FC<IProps> = ({ onClick }) => {
  const { isMobile } = useViewport();
  const { marketName } = useMarketCurrentEvent();
  const { latestMarketEarlyAccessRangeDate } = useMarketRangeDates();
  const matchedDiscountCapBps = useMatchedDiscountCapBps();

  const ValuePropContainer = isMobile ? Column : ValuePropContainerRow;
  const valuePropFontVariant = isMobile
    ? "paragraphSansMedium"
    : "subheadingSansMedium";

  return (
    <Container>
      <ContentContainer>
        <Row>
          <SectionTitle
            variant={
              isMobile ? "pageHeaderSerifRegular" : "displayMSerifRegular"
            }
          >
            <StrictLocalMsg
              id="IEk1hI"
              defaultMessage="Join to sell at {marketEventName}, happening {eventRangeDate}"
              description={{
                text: "Header for the Faire Market banner on the brand landing page",
                img: "https://cdn.faire.com/fastly/3cbf949076e857df1d5b4d155cfa4ae1637fc81bbe6196fbf80be6ddd26fb4e4.png",
              }}
              values={{
                marketEventName: marketName,
                eventRangeDate: latestMarketEarlyAccessRangeDate,
              }}
            />
          </SectionTitle>
          {isMobile ? <MobileMarketIcon /> : <MarketIcon />}
        </Row>
        <Spacer height="4x" />
        <ValuePropContainer>
          <ValuePropColumn>
            <ValuePropTypography variant={valuePropFontVariant}>
              <StrictLocalMsg
                id="YcAi6e"
                defaultMessage="Drive a season's worth of sales in just a few days"
                description={{
                  text: "Explaining to the brand that they do a season's worth of sales during the market event",
                }}
              />
            </ValuePropTypography>
            <Spacer height="1x" />
            <ValuePropTypography>
              <StrictLocalMsg
                id="UoAPfa"
                defaultMessage="Retailers love shopping Faire Markets to discover new brands at great prices. Join for your chance to connect with more buyers than any other time of the year."
              />
            </ValuePropTypography>
          </ValuePropColumn>
          <Spacer width="3x" />
          <ValuePropColumn>
            <ValuePropTypography variant={valuePropFontVariant}>
              <StrictLocalMsg
                id="1EqLYH"
                defaultMessage="Offer your retailers the best prices—on us"
                description={{
                  text: "Explaining that during FWM brands can offer good prices because we have a discount matching program.",
                }}
              />
            </ValuePropTypography>
            <Spacer height="1x" />
            <ValuePropTypography>
              <StrictLocalMsg
                id="0mZKRT"
                defaultMessage="Faire will be offering our popular discount matching program for brands—up to {discountAmountNumber}% off all orders."
                values={{
                  discountAmountNumber: bpsToPercent(matchedDiscountCapBps),
                }}
              />
            </ValuePropTypography>
          </ValuePropColumn>
          <Spacer width="3x" />
          <ValuePropColumn>
            <ValuePropTypography variant={valuePropFontVariant}>
              <StrictLocalMsg
                id="Nackwp"
                defaultMessage="It’s free and convenient to participate"
                description={{
                  text: "Explaining that FWM is free to participate in",
                }}
              />
            </ValuePropTypography>
            <Spacer height="1x" />
            <ValuePropTypography>
              <StrictLocalMsg
                id="AefNu1"
                defaultMessage="There’s no need to step away from your day-to-day business to attend. To participate, simply sign up for Faire and set up a market promotion."
              />
            </ValuePropTypography>
          </ValuePropColumn>
        </ValuePropContainer>
        <Spacer height="5x" />
        <CtaButton variant="secondary" onClick={onClick}>
          <StrictLocalMsg
            id="Qg+gkW"
            defaultMessage="Join Faire"
            description={{ text: "CTA for the brand to join Faire" }}
          />
        </CtaButton>
      </ContentContainer>
    </Container>
  );
};

const Container = styled(Row).attrs({
  justify: "center",
})`
  background-color: ${getEventTheme("faireMarket").primaryDark};

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: ${getSpacing("10x")};
  }

  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing("3x")};
  }
`;

const ContentContainer = styled(Column)`
  ${responsiveMediaQueries.tabletAndAbove} {
    width: 900px;
  }

  ${responsiveMediaQueries.mobile} {
    width: 100%;
  }
`;

const CtaButton = styled(Button)`
  width: 100%;
  border: unset;

  ${responsiveMediaQueries.tabletAndAbove} {
    width: 278px;
  }
`;

const MarketIcon = styled(FaireMarketIcon).attrs({
  size: "124px",
  color: getEventTheme("faireMarket").secondary,
})`
  position: absolute;
  right: 0;
`;

const MobileMarketIcon = styled(FaireMarketIcon).attrs({
  color: getEventTheme("faireMarket").secondary,
  size: "89px",
})`
  position: absolute;
  right: -32px;
`;

const SectionTitle = styled(Typography).attrs({
  color: getEventTheme("faireMarket").promoTextInverse,
})`
  &&& {
    max-width: 850px;
    ${responsiveMediaQueries.tabletAndBelow} {
      margin-right: 72px;
    }
  }
`;

const ValuePropColumn = styled(Column)``;

const ValuePropContainerRow = styled(Row).attrs({
  justify: "space-between",
})``;

const ValuePropTypography = styled(Typography).attrs({
  color: getEventTheme("faireMarket").promoTextInverse,
})``;
